import { objectBy } from '../../util/index.js';
import { billingVoucherSum, } from '../billing-vouchers/index.js';
import { VoucherCategory, VoucherTypeIds } from '../vouchers/index.js';
export var DepositShareType;
(function (DepositShareType) {
    DepositShareType["AdjustmentDebit"] = "ADJUSTMENT_DEBIT";
    DepositShareType["AdjustmentCredit"] = "ADJUSTMENT_CREDIT";
    DepositShareType["Deposit"] = "DEPOSIT";
    DepositShareType["Fee"] = "FEE";
})(DepositShareType || (DepositShareType = {}));
const isDepositShare = (billingVoucher, includeCreditorDepositsInDepositShare) => {
    if (billingVoucher.depositCaseVoucherId) {
        if (billingVoucher.category === VoucherCategory.FEE) {
            if (VoucherTypeIds.COLLECTIONCOMMISSIONCLIENTDEPOSIT ===
                billingVoucher.voucherTypeId) {
                return !!includeCreditorDepositsInDepositShare;
            }
        }
        return true;
    }
    return false;
};
export const getBillingVoucherIdsFromDepositShare = (depositShare) => {
    const billingVoucherIds = [];
    // Overpayments
    for (const v of depositShare.overpayments) {
        billingVoucherIds.push(v.billingVoucherId);
    }
    // Vat
    for (const v of depositShare.vat) {
        billingVoucherIds.push(v.billingVoucherId);
    }
    // Share
    for (const share of depositShare.share) {
        // Vat share
        for (const billingVoucher of share.vatVouchers) {
            billingVoucherIds.push(billingVoucher.billingVoucherId);
        }
        // Collection commission
        for (const billingVoucher of share.collectionCommissionVouchers) {
            billingVoucherIds.push(billingVoucher.billingVoucherId);
        }
        // Payout
        for (const billingVoucher of share.payoutVouchers) {
            billingVoucherIds.push(billingVoucher.billingVoucherId);
        }
    }
    return [...new Set(billingVoucherIds)];
};
/**
 * The vat vouchers of a billing, indexed by the voucher they are the vat of.
 *
 * Built once per billing: looking vat up by scanning the whole list per fee and
 * per disbursement made organizing quadratic, and a billing can carry tens of
 * thousands of vouchers.
 */
export const vatByBillingVoucherId = (billingVouchers) => billingVouchers.reduce((acc, voucher) => {
    if (voucher.category === VoucherCategory.VAT &&
        voucher.vatOfBillingVoucherId) {
        acc[voucher.vatOfBillingVoucherId] = (acc[voucher.vatOfBillingVoucherId] || []).concat(voucher);
    }
    return acc;
}, {});
export const organizeBilling = (billingVouchers, options) => {
    let depositShareMap = {};
    let organizedBillingVouchers = new Set();
    const billingVoucherLookup = objectBy(billingVouchers, (bv) => bv.billingVoucherId);
    for (let i = 0; i < billingVouchers.length; i++) {
        const billingVoucher = billingVouchers[i];
        if (isDepositShare(billingVoucher, options?.includeCreditorDepositsInDepositShare) &&
            // Vat is added to deposit share later in this function
            billingVoucher.category !== VoucherCategory.VAT) {
            const { _depositShareMap } = append(depositShareMap, organizedBillingVouchers, billingVoucher);
            depositShareMap = _depositShareMap;
        }
    }
    // Handle vat on deposit share
    for (let i = 0; i < billingVouchers.length; i++) {
        const billingVoucher = billingVouchers[i];
        if (billingVoucher.category === VoucherCategory.VAT) {
            const vatBillingVoucher = billingVoucher.vatOfBillingVoucherId
                ? billingVoucherLookup[billingVoucher.vatOfBillingVoucherId]
                : null;
            const vatOfCollectionCommissionBillingVoucher = vatBillingVoucher &&
                [VoucherCategory.COLLECTIONCOMMISSION, VoucherCategory.FEE].includes(vatBillingVoucher.category)
                ? vatBillingVoucher
                : null;
            if (vatOfCollectionCommissionBillingVoucher &&
                vatOfCollectionCommissionBillingVoucher.depositCaseVoucherId &&
                vatOfCollectionCommissionBillingVoucher.caseVoucherId) {
                const vatKey = genDepositShareKey(vatOfCollectionCommissionBillingVoucher);
                if (depositShareMap[vatKey]?.share[vatOfCollectionCommissionBillingVoucher.caseVoucherId]) {
                    organizedBillingVouchers.add(billingVoucher.billingVoucherId);
                    depositShareMap[vatKey].vat.push(billingVoucher);
                }
            }
        }
    }
    const vatByOfBillingVoucherId = vatByBillingVoucherId(billingVouchers);
    const withVat = (voucher) => {
        const vat = vatByOfBillingVoucherId[voucher.billingVoucherId] || [];
        vat.forEach((vos) => {
            organizedBillingVouchers.add(vos.billingVoucherId);
        });
        return { ...voucher, vat };
    };
    // Disbursements
    const disbursements = billingVouchers
        .filter((voucher) => voucher.category === VoucherCategory.DISBURSEMENT)
        .map(withVat);
    disbursements.forEach((v) => {
        organizedBillingVouchers.add(v.billingVoucherId);
    });
    // Fees
    const fees = billingVouchers
        .filter((billingVoucher) => billingVoucher.category === VoucherCategory.FEE &&
        !organizedBillingVouchers.has(billingVoucher.billingVoucherId))
        .map(withVat);
    fees.forEach((v) => {
        organizedBillingVouchers.add(v.billingVoucherId);
    });
    // Vat
    const vat = billingVouchers.filter((voucher) => voucher.category === VoucherCategory.VAT &&
        !organizedBillingVouchers.has(voucher.billingVoucherId));
    vat.forEach((v) => {
        organizedBillingVouchers.add(v.billingVoucherId);
    });
    const notOrganizedBillingVouchers = billingVouchers.filter((v) => !organizedBillingVouchers.has(v.billingVoucherId));
    return {
        depositShare: Object.values(depositShareMap).map((depositShare) => ({
            caseId: depositShare.caseId,
            deposit: depositShare.deposit,
            info: depositShare.info,
            overpayments: depositShare.overpayments,
            vat: depositShare.vat,
            share: Object.values(depositShare.share),
            type: depositShare.type,
        })),
        notOrganizedBillingVouchers,
        disbursements,
        fees,
        vat,
    };
};
const genDepositShareKey = (billingVoucher) => {
    return `${billingVoucher.depositCaseVoucherId}${billingVoucher.isAdjustment
        ? `-adjustment-${billingVoucher.amount > 0 ? 'credit' : 'debit'}`
        : ''}${billingVoucher.category === VoucherCategory.FEE ? '-isFee' : ''}`;
};
const append = (depositShareMap, 
/** Borrowed mutably */
organizedBillingVouchers, billingVoucher) => {
    if (!billingVoucher.depositCaseVoucherId ||
        (!billingVoucher.caseVoucherId &&
            billingVoucher.category !== VoucherCategory.OVERPAYMENT)) {
        return {
            _depositShareMap: depositShareMap,
        };
    }
    organizedBillingVouchers.add(billingVoucher.billingVoucherId);
    const key = genDepositShareKey(billingVoucher);
    if (!depositShareMap[key]) {
        depositShareMap[key] = {
            caseId: billingVoucher.caseId,
            deposit: billingVoucher.depositCaseVoucher || null,
            info: billingVoucher.info,
            overpayments: [],
            share: {},
            vat: [],
            type: billingVoucher.isAdjustment
                ? billingVoucher.amount < 0
                    ? DepositShareType.AdjustmentCredit
                    : DepositShareType.AdjustmentDebit
                : billingVoucher.category === VoucherCategory.FEE
                    ? DepositShareType.Fee
                    : DepositShareType.Deposit,
        };
    }
    if (billingVoucher.caseVoucherId &&
        !depositShareMap[key].share[billingVoucher.caseVoucherId]) {
        depositShareMap[key].share[billingVoucher.caseVoucherId] = {
            caseVoucher: billingVoucher.caseVoucher || null,
            collectionCommissionBalance: 0,
            collectionCommissionVouchers: [],
            payoutBalance: 0,
            payoutVouchers: [],
            vatBalance: 0,
            vatVouchers: [],
        };
    }
    if (
    // Collection commission
    billingVoucher.caseVoucherId &&
        [VoucherCategory.COLLECTIONCOMMISSION, VoucherCategory.FEE].includes(billingVoucher.category)) {
        // Add collector vouchers
        depositShareMap[key].share[billingVoucher.caseVoucherId].collectionCommissionVouchers.push(billingVoucher);
        // Add collector voucher balance
        depositShareMap[key].share[billingVoucher.caseVoucherId].collectionCommissionBalance += billingVoucher.amount;
    }
    else if (
    // Payout
    billingVoucher.caseVoucherId &&
        billingVoucher.category === VoucherCategory.PAYOUT) {
        // Add creditor vouchers
        depositShareMap[key].share[billingVoucher.caseVoucherId].payoutVouchers.push(billingVoucher);
        // Add creditor voucher balance
        depositShareMap[key].share[billingVoucher.caseVoucherId].payoutBalance +=
            billingVoucher.amount;
    }
    else if (
    // Overpayment
    billingVoucher.category === VoucherCategory.OVERPAYMENT) {
        depositShareMap[key].overpayments.push(billingVoucher);
    }
    else {
        console.error(billingVoucher);
        throw new Error('Error: Billing voucher not handled correctly');
    }
    return {
        _depositShareMap: depositShareMap,
    };
};
const getVatSum = (vouchers) => vouchers.reduce((acc, voucher) => acc + voucher.vat.reduce((azz, vat) => azz + vat.amount, 0), 0);
export const getBillingSums = (billing) => {
    return {
        balance: -billing.payout + billing.vat + billing.disbursements + billing.revenue,
    };
};
export const getDepositShareSums = (depositShare) => {
    const deposit = depositShare.type === DepositShareType.Fee
        ? 0
        : depositShare.share.reduce((azz, s) => {
            return s.payoutBalance + s.collectionCommissionBalance + azz;
        }, 0);
    const collectionCommission = depositShare.share.reduce((azz, s) => {
        return s.collectionCommissionBalance + azz;
    }, 0);
    const payout = depositShare.share.reduce((azz, s) => {
        return s.payoutBalance + azz;
    }, 0);
    const overpayment = billingVoucherSum(depositShare.overpayments);
    const vat = billingVoucherSum(depositShare.vat);
    return {
        deposit,
        vat,
        collectionCommission,
        payout,
        overpayment,
        balance: collectionCommission + vat - deposit,
    };
};
const bVFilter = (filter) => (billingVoucher) => {
    if (filter?.caseId) {
        return billingVoucher.caseId === filter.caseId;
    }
    return true;
};
const dSFilter = (filter) => (depositShare) => {
    if (filter?.caseId) {
        return depositShare.caseId === filter.caseId;
    }
    return true;
};
export const getSums = (billing, filter) => {
    const depositSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, depositShare) => {
        if (depositShare.type === DepositShareType.Fee) {
            return acc;
        }
        const { deposit } = getDepositShareSums(depositShare);
        return deposit + acc;
    }, 0);
    const collectionCommissionSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, depositShare) => {
        if (depositShare.type === DepositShareType.Fee) {
            return acc;
        }
        const { collectionCommission } = getDepositShareSums(depositShare);
        return collectionCommission + acc;
    }, 0);
    const collectionCommissionVatSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, { vat, type }) => {
        if (type === DepositShareType.Fee) {
            return acc;
        }
        return acc + billingVoucherSum(vat);
    }, 0);
    const collectionCommissionTypeFeeSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, depositShare) => {
        if (depositShare.type !== DepositShareType.Fee) {
            return acc;
        }
        const { collectionCommission } = getDepositShareSums(depositShare);
        return collectionCommission + acc;
    }, 0);
    const collectionCommissionTypeFeeVatSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, { vat, type }) => {
        if (type !== DepositShareType.Fee) {
            return acc;
        }
        return acc + billingVoucherSum(vat);
    }, 0);
    const disbursementsSum = billing.disbursements
        .filter(bVFilter(filter))
        .reduce((acc, voucher) => acc + voucher.amount, 0);
    const feesSum = billing.fees
        .filter(bVFilter(filter))
        .reduce((acc, voucher) => acc + voucher.amount, 0) +
        collectionCommissionTypeFeeSum;
    const vatSum = billing.vat
        .filter(bVFilter(filter))
        .reduce((acc, voucher) => acc + voucher.amount, 0);
    const payoutSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, depositShare) => {
        const { payout } = getDepositShareSums(depositShare);
        return payout + acc;
    }, 0);
    const overpaymentsSum = billing.depositShare
        .filter(dSFilter(filter))
        .reduce((acc, depositShare) => {
        const { overpayment } = getDepositShareSums(depositShare);
        return overpayment + acc;
    }, 0);
    const disbursementsVatSum = getVatSum(billing.disbursements.filter(bVFilter(filter)));
    const feesVatSum = getVatSum(billing.fees.filter(bVFilter(filter))) +
        collectionCommissionTypeFeeVatSum;
    const subTotal = -depositSum + collectionCommissionSum + feesSum + disbursementsSum;
    const vat = vatSum + disbursementsVatSum + feesVatSum + collectionCommissionVatSum;
    const sums = {
        disbursementsSum,
        disbursementsVatSum,
        feesSum,
        feesVatSum,
        depositSum,
        vatSum,
        collectionCommissionSum,
        collectionCommissionVatSum,
        payoutSum,
        overpaymentsSum,
        vat,
        subTotal,
        balance: subTotal + vat,
    };
    return sums;
};
//# sourceMappingURL=organize.js.map